import React, { useRef, useState } from 'react';
import AppHeader from '../../common/app-header/AppHeader';
import { Box, CircularProgress, Grid, Stack, Tooltip, Typography } from '@mui/material';
import { useDocuments } from '../document-vault/hooks/useDocuments';

import { useTranslation } from 'react-i18next';
import BasicTabs from '../../common/basic-tabs/BasicTabs';
import DetailsTabs from './components/DetailsTab';
import DocumentsTabs from './components/DocumentsTabs';
import NotesTab from '../../common/notes-tab/NotesTab';
import FormUpload from '../../common/form-upload/FormUpload';
import UploadButton from '../../common/upload-button/UploadButton';
import { useAppSelector } from '../../common/hooks/useAppSelector';
import styles from './CustomerProfile.module.scss';
import { selectAllCompanies } from '../../slices/companies/companiesSlice';
import { Link, useParams } from 'react-router-dom';
import CreateCustomerContactModal from '../../common/create-customer-contact-modal/CreateCustomerContactModal';
import Button from '@mui/material/Button';
import { ROUTES } from '../../common/constants/routes';
import { useCustomerDetails } from './hooks/useCustomerDetails';
import { CreateCustomerContactItemModal } from '../../common/create-customer-contact-item-modal/CreateCustomerContactItemModal';
import ContactsTabs from './components/ContactsTabs';
import InfoIcon from '@mui/icons-material/Info';
import PreviewTooltip from '../../common/preview-block/PreviewTooltip';
import Preview from './components/preview-block/Preview';
import EditNoteOutlinedIcon from '@mui/icons-material/EditNoteOutlined';
import { useDocumentSigning } from '../document-vault/hooks/useDocumentSigning';
import { useAppDispatch } from '../../common/hooks/useAppDispatch';
import { showOverlay } from '../../slices/show-progress-overlay/showProgressOverlay';
import { DocumentVaultItemModel } from '../../models/document.vault.item.model';
import { useAssignProfilePersonModal } from '../../common/assign-profile-person-modal/useAssignProfilePersonModal';
import { AssignProfilePersonModal } from '../../common/assign-profile-person-modal/AssignProfilePersonModal';
import { Document } from '../document-vault/components/document-list-vault/types';
import { useProfileDocumentsQuery } from '../document-vault/hooks/useProfileDocumentsQuery';
import { DOCUMENTS_PER_PAGE } from '../document-vault/hooks/vaultQueryParams';
import { RootState } from '../../store';
import ContactMailOutlinedIcon from '@mui/icons-material/ContactMailOutlined';

const CustomerProfile = () => {
  const dispatch = useAppDispatch();
  const [isUploadOpen, setIsUploadOpen] = useState(false);
  const [isUploadAndSignOpen, setIsUploadAndSignOpen] = useState(false);
  const company = useAppSelector(selectAllCompanies)[0];
  const documentsLoading = useAppSelector((state: RootState) => state.documents.status);
  const formUploadRef = useRef<HTMLDivElement>(null);
  const uploadButtonRef = useRef<HTMLButtonElement>(null);
  const { customerId } = useParams();
  const parsedCustomerId = Number(customerId);
  const sortByDate = (a: any, b: any) => {
    return Number(new Date(b.created)) - Number(new Date(a.created));
  };

  // Profile tab, selected workflow, card/list view and page live in the URL query,
  // so the browser Back button from a document details page restores the same view.
  const {
    tab: activeTab,
    setTab: setActiveTab,
    workflow,
    setWorkflow,
  } = useProfileDocumentsQuery();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);

  const { customer } = useCustomerDetails(parsedCustomerId);

  const handleUploadAndSignClose = () => {
    setIsUploadAndSignOpen(false);
  };

  const {
    documents,
    documentsCount,
    documentsPage,
    getRequestedPage,
    setShowWorkflowBreadcrumbs,
    showWorkflowBreadcrumbs,
    cardView,
    setCardView,
  } = useDocuments('customers', workflow ?? undefined, parsedCustomerId);
  const combinedDocuments = [...documents].slice().sort((a, b) => sortByDate(a, b));

  const {
    document: documentToSign,
    setDocument: setDocumentToSign,
    handleSignOriginalDocument,
    handleSignLatestVersion,
    setOpenAssignPersonDialog,
    openAssignPersonDialog,
  } = useDocumentSigning(cardView);

  const {
    patchDocumentWithProfilePerson,
    setAssignee,
    assignee /*checkProfilePersonsAvailability*/,
  } = useAssignProfilePersonModal(documentToSign?.category, documentToSign);

  const handleUploadAndSignOpen = () => {
    setIsUploadAndSignOpen(true);
  };

  const handleUploadAndSignSuccess = (uploadedDocument: DocumentVaultItemModel, tab?: Window) => {
    const document = uploadedDocument as Document;
    /*if (
      document.customers.length === 0 &&
      document.employees.length === 0 &&
      document.suppliers.length === 0 &&
      (document.business_profile_contacts?.length ?? 0) === 0 &&
      checkProfilePersonsAvailability(document.category)
    ) {
      dispatch(hideOverlay());
      if (tab) {
        tab.close();
      }
      setDocumentToSign(document);
      setOpenAssignPersonDialog(true);
    } else {*/
    handleSignOriginalDocument(document, undefined, undefined, tab);
    //}
  };

  // Items per page - adjust this value according to your API
  // Calculate total pages
  const totalPages = Math.ceil(documentsCount / DOCUMENTS_PER_PAGE);

  // Handle page change
  const handlePageChange = (event: React.ChangeEvent<unknown>, page: number) => {
    // Written to the URL (?page=N); the documents are fetched from it
    getRequestedPage(page);
  };

  const { t } = useTranslation();

  const handleUploadClose = () => {
    setIsUploadOpen(false);
  };

  // Actually handles both open & close
  const handleUploadOpen = () => {
    setIsUploadOpen((prev) => !prev);
  };

  /*useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const targetElement = event.target as HTMLElement;
      if (
        targetElement.closest('.MuiMenuItem-root') || // Ignore clicks on MenuItem
        targetElement.closest('.MuiSelect-root') // Ignore clicks on Select
      ) {
        return;
      }
      if (
        formUploadRef.current &&
        !formUploadRef.current.contains(event.target as Node) &&
        uploadButtonRef.current &&
        !uploadButtonRef.current.contains(event.target as Node)
      ) {
        setIsUploadOpen(false);
      }
    };

    if (isUploadOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isUploadOpen]);*/

  const toggleShowAddPreferredContactMethodModal = () => {
    setIsModalOpen((prev) => !prev);
  };

  const toggleShowAddContactModal = () => {
    setIsContactModalOpen((prev) => !prev);
  };

  const handleSuccessfulSubmit = () => {
    setIsModalOpen((prev) => !prev);
    setActiveTab(1);
  };

  const handleSuccessfulSubmitContactModal = () => {
    setIsContactModalOpen(false);
    setActiveTab(2);
  };

  if (!company || !customer) {
    return (
      <>
        <AppHeader title={t('pages.customerProfiles')} />
        <Box
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: 'calc(100vh - 104px)',
            flexWrap: 'wrap',
            rowGap: '8px',
          }}
        >
          <CircularProgress color='primary' size={50} />
        </Box>
      </>
    );
  }

  return (
    <>
      <AppHeader title={t('pages.customerProfiles')} />
      <Grid component='div' container spacing={3} sx={{ marginTop: '16px' }}>
        <Grid component='div' size={{ xs: 12 }}>
          <Stack component='div' spacing={2}>
            <Box className={styles.breadcrumbs}>
              <Link
                id='customerProfileCustomerProfilesLink'
                className={styles.documentViewText}
                to={ROUTES.CUSTOMER_PROFILES}
              >
                {t('pages.customerProfiles')}
              </Link>
              <Typography variant='body2' className={styles.separator}>
                ›
              </Typography>
              <Typography variant='body2' className={styles.documentViewText}>
                {customer.customer_name}
              </Typography>
            </Box>
            <Typography variant='h1' component='h1' className={styles.page_title}>
              {customer.customer_name}
            </Typography>
            <Stack
              component='div'
              direction='row'
              spacing={1}
              useFlexGap
              sx={{
                alignItems: 'center',
                flexWrap: 'wrap',
                display: { xs: 'block', md: 'flex' },
                marginTop: '8px',
              }}
            >
              <Stack
                sx={{
                  alignItems: 'center',
                  flexDirection: 'row',
                  flexWrap: 'wrap',
                  gap: '8px',
                }}
              >
                <Box ref={uploadButtonRef} style={{ position: 'relative' }}>
                  <UploadButton
                    id='customerProfileUploadFilesBtn'
                    onClick={handleUploadOpen}
                    buttonText={t('buttons.uploadFiles')}
                    isArrowDown={false}
                  />
                  {isUploadOpen && (
                    <Box
                      className={styles.form_box}
                      ref={formUploadRef}
                      style={{ alignSelf: 'flex-start' }}
                    >
                      <FormUpload
                        onCancel={handleUploadClose}
                        categoryValue='customers'
                        customers={[customer]}
                        workflow={workflow}
                      />
                    </Box>
                  )}
                </Box>

                <Box style={{ position: 'relative' }}>
                  <UploadButton
                    id='customerProfileUploadAndSignPdfBtn'
                    onClick={handleUploadAndSignOpen}
                    buttonText={t('buttons.uploadAndSignPDF')}
                    isArrowDown={false}
                    icon={EditNoteOutlinedIcon}
                  />
                  {isUploadAndSignOpen && (
                    <Box className={styles.form_box} style={{ alignSelf: 'flex-start' }}>
                      <FormUpload
                        onCancel={handleUploadAndSignClose}
                        categoryValue='customers'
                        customers={[customer]}
                        workflow={workflow}
                        uploadWithSigning={true}
                        accept='.pdf'
                        supportedFormatsKey='messages.supportedFormatsPDF'
                        maxFiles={1}
                        onUploadSuccess={handleUploadAndSignSuccess}
                      />
                    </Box>
                  )}
                </Box>

                <Tooltip title={t('buttons.addPreferredContactMethod')} arrow placement='top'>
                  <Button
                    id='customerProfileAddPreferredContactMethodBtn'
                    onClick={toggleShowAddPreferredContactMethodModal}
                    className={styles.add_button}
                  >
                    {t('buttons.addPreferredContactMethod')}
                  </Button>
                </Tooltip>
                <Tooltip title={t('buttons.addContact')} arrow placement='top'>
                  <Button
                    id='customerProfileCreateContactBtn'
                    onClick={toggleShowAddContactModal}
                    className={styles.add_button}
                  >
                    <ContactMailOutlinedIcon id='customerProfileCreateContactBtnIcon' />
                    <span id='customerProfileCreateContactBtnSpan'>
                      {t('buttons.createContact')}
                    </span>
                  </Button>
                </Tooltip>
                <PreviewTooltip title={<Preview />} arrow placement='bottom'>
                  <InfoIcon
                    color='primary'
                    fontSize='small'
                    sx={{ display: { xs: 'none', md: 'inline-block' } }}
                  />
                </PreviewTooltip>
              </Stack>
            </Stack>
          </Stack>
        </Grid>
        <Grid component='div' size={{ xs: 12 }} className={styles.grid_margin_bottom}>
          <BasicTabs
            tabs={[
              /*t('businessProfile.allTab'),*/
              t('businessProfile.documentsTabs'),
              t('businessProfile.detailsTab'),
              t('businessProfile.contactsTab'),
              t('businessProfile.notesTab'),
            ]}
            activeTab={activeTab}
            onTabChange={setActiveTab}
            tabContents={[
              /*<AllTabs
                            customer={customer}
                            company={company}
                            documents={combinedDocuments}
                            key='all'
                          />,*/
              <DocumentsTabs
                documents={combinedDocuments}
                key='documents'
                cardView={cardView}
                workflow={workflow}
                setWorkflow={setWorkflow}
                showWorkflowBreadcrumbs={showWorkflowBreadcrumbs}
                setShowWorkflowBreadcrumbs={setShowWorkflowBreadcrumbs}
                documentsActions={{
                  idPrefix: 'customerProfile',
                  cardView,
                  onCardView: () => setCardView(true),
                  onListView: () => setCardView(false),
                  listViewDisabled: !!workflow && showWorkflowBreadcrumbs,
                  showPagination: documents.length > 0 && (!!workflow || !cardView),
                  page: documentsPage,
                  totalPages,
                  onPageChange: handlePageChange,
                  paginationDisabled: documentsLoading === 'loading',
                }}
              />,
              <DetailsTabs customer={customer} company={company} key='details' />,
              <ContactsTabs customer={customer} companyId={company?.id ?? 0} key='contacts' />,
              <NotesTab customer={customer} key='notes' />,
            ]}
          />
        </Grid>
        {isModalOpen && (
          <CreateCustomerContactModal
            customer={customer}
            companyId={company.id ?? 0}
            open={isModalOpen}
            onClose={() => toggleShowAddPreferredContactMethodModal()}
            onSuccessfulSubmit={() => handleSuccessfulSubmit()}
          />
        )}
        {isContactModalOpen && (
          <CreateCustomerContactItemModal
            customerId={customer.id}
            companyId={company.id ?? 0}
            open={isContactModalOpen}
            onClose={() => setIsContactModalOpen(false)}
            onSuccessfulSubmit={() => handleSuccessfulSubmitContactModal()}
          />
        )}
        {openAssignPersonDialog && documentToSign && (
          <AssignProfilePersonModal
            open={openAssignPersonDialog}
            onClose={() => {
              setOpenAssignPersonDialog(false);
              setDocumentToSign(null as unknown as Document);
              setAssignee('');
            }}
            modalTitle={t('headers.startSigning')}
            category={documentToSign?.category}
            document={documentToSign}
            goBackButtonText={t('buttons.goBack')}
            modalMessage={t('messages.signingRecommendations')}
            assignee={assignee}
            setAssignee={setAssignee}
            signOriginalDocument={true}
            secondaryAction={() => {
              dispatch(showOverlay());
              const url = `${window.location.origin}${ROUTES.PROCESSING}`;
              const tab = window.open('', '_blank');
              // @ts-expect-error: there can be an error
              tab.location.href = url;
              if (assignee && assignee !== 'noAssignee') {
                patchDocumentWithProfilePerson()
                  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                  //@ts-ignore
                  .then((res) => handleSignOriginalDocument(res, undefined, undefined, tab))
                  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                  //@ts-ignore
                  .catch(() =>
                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                    //@ts-ignore
                    handleSignOriginalDocument(documentToSign, undefined, undefined, tab)
                  );
              } else {
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                //@ts-ignore
                handleSignOriginalDocument(documentToSign, undefined, true, tab);
              }
            }}
            primaryAction={() => {
              dispatch(showOverlay());
              const url = `${window.location.origin}${ROUTES.PROCESSING}`;
              const tab = window.open('', '_blank');
              // @ts-expect-error: there can be an error
              tab.location.href = url;
              if (assignee && assignee !== 'noAssignee') {
                patchDocumentWithProfilePerson()
                  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                  //@ts-ignore
                  .then((res) => handleSignLatestVersion(res, undefined, undefined, undefined, tab))
                  .catch(() =>
                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                    //@ts-ignore
                    handleSignLatestVersion(documentToSign, undefined, undefined, undefined, tab)
                  );
              } else {
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                //@ts-ignore
                handleSignLatestVersion(documentToSign, undefined, undefined, undefined, tab);
              }
            }}
          />
        )}
      </Grid>
    </>
  );
};

export default CustomerProfile;
